import type {
  ConditionGroup,
  GroupedIntakeFormSummary,
  IntakeForm,
  IntakeFormAggregatedResponse,
  IntakeFormConsentDocumentDelivery,
  IntakeFormField,
  IntakeFormHouseholdMemberMapping,
  IntakeFormInvitation,
  IntakeFormInvitationSummary,
  IntakeFormLayout,
  IntakeFormLayoutKey,
  IntakeFormSubmission,
  IntakeFormSubmissionSummary,
  IntakeFormSummary,
  IntakeFormTemplate,
  NextPageParams,
  PaginationMetadata,
  SharedIntakeForm,
} from "@/schema"

import { ApiContext, apiContextBlobFetch, apiContextFetch, apiContextTextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"
import type {
  PublicIntakeFormDraft,
  PublicIntakeFormDraftPayload,
  PublicIntakeFormUpload,
} from "@/hooks/api/public-intake-forms"

export interface IntakeFormsResponse {
  intake_forms: IntakeFormSummary[]
  pagination?: PaginationMetadata
}

export interface GroupedIntakeFormsResponse {
  groups: Record<GroupedIntakeFormSummary["groupKey"], { intake_forms: IntakeFormSummary[]; total_count: number }>
  pagination: PaginationMetadata
}

export type CreateIntakeFormAttributes =
  | {
      intake_form_template_key: string
      layout_key: IntakeFormLayoutKey
      scheduling_event_type_id?: string
      template_variables?: Record<string, string>
      prompt?: never
      files?: never
    }
  | { prompt: string; files: File[]; intake_form_template_key?: never; layout_key?: never }

export interface IntakeFormTemplatesResponse {
  intake_form_templates: IntakeFormTemplate[]
  layouts: IntakeFormLayout[]
}

export interface IntakeFormInvitationsResponse {
  intake_form_invitations: IntakeFormInvitationSummary[]
  pagination?: PaginationMetadata
}

export interface AdvisorIntakeFormDraftContext {
  version_id: string
  form_name: string
  prefill_answers: Record<string, unknown>
  recipient_editing: boolean
  draft: PublicIntakeFormDraft | null
}

export interface IntakeFormSubmissionsResponse {
  intake_form_submissions: IntakeFormSubmissionSummary[]
  pagination?: PaginationMetadata
}

export interface IntakeFormAggregatedResponsesResponse {
  fields: IntakeFormField[]
  intake_form_submissions: IntakeFormAggregatedResponse[]
}

export function bookResource(context: ApiContext, resource: string) {
  if (!context.bookId) throw new Error("A book is required for intake forms")
  return `books/${encodeURIComponent(context.bookId)}/${resource}`
}

function fetchHtml(context: ApiContext, route: string) {
  return apiContextTextFetch(context, route, { cache: "no-store" })
}

export function fetchIntakeFormTemplates(context: ApiContext) {
  return apiContextFetch<IntakeFormTemplatesResponse>(context, bookResource(context, "intake_form_templates"))
}

export function fetchIntakeFormTemplatePreview(
  context: ApiContext,
  templateKey: string,
  layoutKey: IntakeFormLayoutKey
) {
  return fetchHtml(
    context,
    bookResource(
      context,
      `intake_form_templates/${encodeURIComponent(templateKey)}/previews/${encodeURIComponent(layoutKey)}`
    )
  )
}

export function fetchIntakeForms(
  context: ApiContext,
  page?: NextPageParams,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const path = bookResource(context, "intake_forms")
  const params = new URLSearchParams()
  if (page) {
    params.set("page", String(page.page))
    params.set("per_page", String(page.per_page))
  }
  appendViewSortParams(params, viewSort)

  const query = params.toString()
  return apiContextFetch<IntakeFormsResponse>(context, query ? `${path}?${query}` : path)
}

export function fetchGroupedIntakeForms(
  context: ApiContext,
  conditionGroup?: ConditionGroup,
  search?: string,
  page?: NextPageParams,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const path = bookResource(context, "intake_forms")
  const params = new URLSearchParams({ grouped: "true" })
  if (page) {
    params.set("page", String(page.page))
    params.set("per_page", String(page.per_page))
  }
  if (conditionGroup) {
    appendConditionGroupParams(params, conditionGroup)
  }
  if (search) {
    params.set("search", search)
  }
  appendViewSortParams(params, viewSort)

  return apiContextFetch<GroupedIntakeFormsResponse>(context, `${path}?${params.toString()}`)
}

export function fetchIntakeForm(context: ApiContext, id: string) {
  return apiContextFetch<IntakeForm>(context, bookResource(context, `intake_forms/${encodeURIComponent(id)}`))
}

export function fetchSharedIntakeForm(context: ApiContext, guid: string) {
  return apiContextFetch<SharedIntakeForm>(
    context,
    bookResource(context, `shared_intake_forms/${encodeURIComponent(guid)}`)
  )
}

export function installSharedIntakeForm(context: ApiContext, guid: string, schedulingEventTypeId?: string) {
  return apiContextFetch<IntakeForm>(
    context,
    bookResource(context, `shared_intake_forms/${encodeURIComponent(guid)}/installation`),
    {
      method: "POST",
      body: JSON.stringify({
        shared_intake_form_installation: {
          scheduling_event_type_id: schedulingEventTypeId,
        },
      }),
    }
  )
}

export async function createIntakeForm(context: ApiContext, attributes: CreateIntakeFormAttributes) {
  const requestAttributes =
    "files" in attributes
      ? {
          prompt: attributes.prompt,
          source_document_signed_ids: await uploadIntakeFormFiles(context, attributes.files ?? []),
        }
      : attributes

  return apiContextFetch<IntakeForm>(context, bookResource(context, "intake_forms"), {
    method: "POST",
    body: JSON.stringify({ intake_form: requestAttributes }),
  })
}

export function duplicateIntakeForm(context: ApiContext, id: string) {
  return apiContextFetch<IntakeForm>(
    context,
    bookResource(context, `intake_forms/${encodeURIComponent(id)}/duplicate`),
    { method: "POST" }
  )
}

export function updateIntakeForm(
  context: ApiContext,
  id: string,
  attributes: {
    name?: string
    status?: IntakeForm["status"]
    layout_key?: IntakeFormLayoutKey
    field_mappings?: Record<string, string>
    household_member_mappings?: Record<string, IntakeFormHouseholdMemberMapping>
    notification_recipient_user_ids?: string[]
    default_email_subject?: string | null
    default_email_body?: string | null
  }
) {
  return apiContextFetch<IntakeForm>(context, bookResource(context, `intake_forms/${encodeURIComponent(id)}`), {
    method: "PATCH",
    body: JSON.stringify({ intake_form: attributes }),
  })
}

export function shareIntakeFormWithBook(context: ApiContext, intakeFormId: string, bookId: string) {
  return apiContextFetch<{ id: string; book_id: string }>(
    context,
    bookResource(context, `intake_forms/${encodeURIComponent(intakeFormId)}/book_intake_forms`),
    {
      method: "POST",
      body: JSON.stringify({ book_intake_form: { book_id: bookId } }),
    }
  )
}

export function unshareIntakeFormFromBook(context: ApiContext, intakeFormId: string, bookIntakeFormId: string) {
  return apiContextFetch<void>(
    context,
    bookResource(
      context,
      `intake_forms/${encodeURIComponent(intakeFormId)}/book_intake_forms/${encodeURIComponent(bookIntakeFormId)}`
    ),
    { method: "DELETE" }
  )
}

export function submitIntakeFormFeedback(context: ApiContext, id: string, message: string) {
  return apiContextFetch<void>(context, bookResource(context, `intake_forms/${encodeURIComponent(id)}/feedback`), {
    method: "POST",
    body: JSON.stringify({ feedback: { message } }),
  })
}

async function uploadIntakeFormFiles(context: ApiContext, files: File[]) {
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace(/\/api\/?$/, "")
  return Promise.all(files.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
}

export function fetchIntakeFormPreview(
  context: ApiContext,
  id: string,
  householdId?: string | null,
  published?: boolean,
  schedulingEventTypeId?: string | null
) {
  const path = bookResource(context, `intake_forms/${encodeURIComponent(id)}/preview`)
  const params = new URLSearchParams()
  if (householdId) params.set("household_id", householdId)
  if (published) params.set("published", "true")
  if (schedulingEventTypeId) params.set("scheduling_event_type_id", schedulingEventTypeId)
  const query = params.toString()
  return fetchHtml(context, query ? `${path}?${query}` : path)
}

export function fetchIntakeFormInvitations(
  context: ApiContext,
  conditionGroup?: ConditionGroup,
  page?: NextPageParams,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  intakeFormId?: string,
  activity: "all" | "opened" = "all",
  householdId?: string,
  withoutSubmission = false
) {
  const params = new URLSearchParams()
  if (page) {
    params.set("page", String(page.page))
    params.set("per_page", String(page.per_page))
  }
  if (conditionGroup) appendConditionGroupParams(params, conditionGroup)
  appendViewSortParams(params, viewSort)
  if (intakeFormId) params.set("intake_form_id", intakeFormId)
  if (activity !== "all") params.set("activity", activity)
  if (householdId) params.set("household_id", householdId)
  if (withoutSubmission) params.set("without_submission", "true")

  const query = params.toString()
  const resource = `intake_form_invitations${query ? `?${query}` : ""}`
  return apiContextFetch<IntakeFormInvitationsResponse>(context, bookResource(context, resource))
}

export function fetchIntakeFormInvitation(context: ApiContext, id: string) {
  return apiContextFetch<IntakeFormInvitation>(
    context,
    bookResource(context, `intake_form_invitations/${encodeURIComponent(id)}`)
  )
}

export function fetchIntakeFormInvitationDraft(context: ApiContext, id: string) {
  return apiContextFetch<AdvisorIntakeFormDraftContext>(
    context,
    bookResource(context, `intake_form_invitations/${encodeURIComponent(id)}/draft_submission`)
  )
}

export function fetchIntakeFormInvitationForm(context: ApiContext, id: string) {
  return fetchHtml(context, bookResource(context, `intake_form_invitations/${encodeURIComponent(id)}/form`))
}

export function saveIntakeFormInvitationDraft(context: ApiContext, id: string, draft: PublicIntakeFormDraftPayload) {
  return apiContextFetch<AdvisorIntakeFormDraftContext>(
    context,
    bookResource(context, `intake_form_invitations/${encodeURIComponent(id)}/draft_submission`),
    {
      method: draft.id ? "PATCH" : "POST",
      body: JSON.stringify({
        draft_submission: {
          client_submission_id: draft.client_submission_id,
          answers: draft.answers,
          documents: draft.documents,
          draft_state: {
            attestations: draft.attestations,
            touched_keys: draft.touched_keys,
            nav: draft.nav,
          },
        },
      }),
    }
  )
}

export function uploadIntakeFormInvitationDraftDocument(
  context: ApiContext,
  id: string,
  input: { clientSubmissionId: string; fieldKey: string; file: File }
) {
  const formData = new FormData()
  formData.append("client_submission_id", input.clientSubmissionId)
  formData.append("field_key", input.fieldKey)
  formData.append("file", input.file)
  return apiContextFetch<PublicIntakeFormUpload>(
    context,
    bookResource(context, `intake_form_invitations/${encodeURIComponent(id)}/uploads`),
    { method: "POST", body: formData }
  )
}

export interface CreateIntakeFormInvitationAttributes {
  intake_form_id: string
  household_id: string
  form_name: string
  delivery_method: "slant_email" | "personal_email"
  email_integration_id: string | null | undefined
  email_subject: string | null
  email_body: string | null
  creator_copy_method: "bcc" | "cc" | "none"
  recipient_emails: Record<string, string>
  recipient_preferences: Record<string, "to" | "cc" | "bcc" | "none">
  prefill_answers: Record<string, unknown>
  reminder_interval_days: number | null
  reminder_limit: number | null
  scheduling_event_type_id?: string | null
}

export interface CreateIntakeFormInvitationBatchAttributes {
  intake_form_id: string
  household_ids: string[]
  form_name: string
  creator_copy_method: "bcc" | "cc" | "none"
  reminder_interval_days: number | null
  reminder_limit: number | null
  scheduling_event_type_id?: string | null
}

export interface CreateIntakeFormInvitationBatchResponse {
  created_count: number
  invitation_ids: string[]
  message: string
}

export function createIntakeFormInvitation(context: ApiContext, attributes: CreateIntakeFormInvitationAttributes) {
  return apiContextFetch<IntakeFormInvitation>(context, bookResource(context, "intake_form_invitations"), {
    method: "POST",
    body: JSON.stringify({ intake_form_invitation: attributes }),
  })
}

export function createIntakeFormInvitationBatch(
  context: ApiContext,
  attributes: CreateIntakeFormInvitationBatchAttributes
) {
  return apiContextFetch<CreateIntakeFormInvitationBatchResponse>(
    context,
    bookResource(context, "intake_form_invitation_batches"),
    {
      method: "POST",
      body: JSON.stringify({ intake_form_invitation_batch: attributes }),
    }
  )
}

export function createIntakeFormTestInvitation(context: ApiContext, intakeFormId: string) {
  return apiContextFetch<{ email: string }>(context, bookResource(context, "intake_form_test_invitations"), {
    method: "POST",
    body: JSON.stringify({ intake_form_test_invitation: { intake_form_id: intakeFormId } }),
  })
}

export function revokeIntakeFormInvitation(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, bookResource(context, `intake_form_invitations/${encodeURIComponent(id)}`), {
    method: "DELETE",
  })
}

export function cancelIntakeFormInvitationReminderSchedule(context: ApiContext, reminderId: string) {
  return apiContextFetch<IntakeFormInvitation>(
    context,
    bookResource(context, `intake_form_invitation_reminder_schedules/${encodeURIComponent(reminderId)}`),
    { method: "DELETE" }
  )
}

export function fetchIntakeFormSubmissions(
  context: ApiContext,
  page?: NextPageParams,
  intakeFormId?: string,
  activity: "all" | "needs_review" | "unread" = "all",
  householdId?: string,
  conditionGroup?: ConditionGroup,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const params = new URLSearchParams()
  if (page) {
    params.set("page", String(page.page))
    params.set("per_page", String(page.per_page))
  }
  if (intakeFormId) params.set("intake_form_id", intakeFormId)
  if (householdId) params.set("household_id", householdId)
  if (activity !== "all") params.set("activity", activity)
  if (conditionGroup) appendConditionGroupParams(params, conditionGroup)
  appendViewSortParams(params, viewSort)

  const query = params.toString()
  const resource = `intake_form_submissions${query ? `?${query}` : ""}`
  return apiContextFetch<IntakeFormSubmissionsResponse>(context, bookResource(context, resource))
}

export function fetchIntakeFormAggregatedResponses(context: ApiContext, intakeFormId: string) {
  return apiContextFetch<IntakeFormAggregatedResponsesResponse>(
    context,
    bookResource(context, `intake_forms/${encodeURIComponent(intakeFormId)}/aggregated_responses`)
  )
}

/**
 * A public submission has no target until it is applied, so its field changes compare against
 * nothing. Passing targetId scores them against the household the advisor is considering, which
 * is what the apply contract's reviewed_values must match.
 */
export function fetchIntakeFormSubmission(
  context: ApiContext,
  id: string,
  targetId?: string | null,
  additionalMappings: Record<string, string> = {},
  personMappings: Record<string, string> = {}
) {
  const path = bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}`)
  const params = new URLSearchParams()
  if (targetId) params.set("target_id", targetId)
  Object.entries(additionalMappings).forEach(([fieldKey, targetPath]) =>
    params.set(`additional_mappings[${fieldKey}]`, targetPath)
  )
  Object.entries(personMappings).forEach(([fieldGroup, personId]) =>
    params.set(`person_mappings[${fieldGroup}]`, personId)
  )
  if (params.size === 0) return apiContextFetch<IntakeFormSubmission>(context, path)

  return apiContextFetch<IntakeFormSubmission>(context, `${path}?${params.toString()}`)
}

export function deleteIntakeFormSubmission(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}`), {
    method: "DELETE",
  })
}

export function markIntakeFormSubmissionRead(context: ApiContext, id: string) {
  return apiContextFetch<void>(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/read`),
    {
      method: "POST",
    }
  )
}

export function revealIntakeFormSubmissionSensitiveAnswer(context: ApiContext, id: string, fieldKey: string) {
  return apiContextFetch<{ field_key: string; value: string }>(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/sensitive_answer_reveal`),
    {
      method: "POST",
      body: JSON.stringify({ field_key: fieldKey }),
    }
  )
}

export function dismissIntakeFormSubmission(context: ApiContext, id: string) {
  return apiContextFetch<void>(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/dismissal`),
    { method: "POST" }
  )
}

export function restoreIntakeFormSubmission(context: ApiContext, id: string) {
  return apiContextFetch<void>(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/dismissal`),
    { method: "DELETE" }
  )
}

export function downloadIntakeFormSubmissionConsentDocument(context: ApiContext, id: string) {
  return apiContextBlobFetch(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/consent_document`)
  )
}

export function downloadIntakeFormSubmissionPdfExport(context: ApiContext, id: string, fullForm = false) {
  return apiContextBlobFetch(
    context,
    bookResource(
      context,
      `intake_form_submissions/${encodeURIComponent(id)}/pdf_export?content=${fullForm ? "full_form" : "summary"}`
    )
  )
}

export function downloadIntakeFormSubmissionCsvExport(context: ApiContext, id: string) {
  return apiContextBlobFetch(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/csv_export`)
  )
}

export function downloadIntakeFormCsvExport(context: ApiContext, id: string) {
  return apiContextBlobFetch(context, bookResource(context, `intake_forms/${encodeURIComponent(id)}/csv_export`))
}

export function saveIntakeFormSubmissionConsentDocument(
  context: ApiContext,
  id: string,
  attributes: { storage_folder_id?: string; storage_folder_name?: string } = {}
) {
  return apiContextFetch<IntakeFormConsentDocumentDelivery>(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/consent_document`),
    {
      method: "POST",
      body: JSON.stringify({ consent_document: attributes }),
    }
  )
}

export interface CreateIntakeFormApplicationAttributes {
  field_keys: string[]
  reviewed_values: Record<string, unknown>
  new_values: Record<string, unknown>
  additional_mappings: Record<string, string>
  person_mappings: Record<string, string>
  create_as_note?: boolean
  marital_status_changes: {
    source_field_key: string
    field_group: "head_of_household" | "spouse_or_partner"
    person_id: string
    reviewed_value: string
    mirrored: boolean
  }[]
  member_resolutions: {
    key: string
    action: "create" | "existing" | "ignore"
    person_id?: string
    reviewed_updated_at?: string
  }[]
  target?: {
    mode: "direct" | "existing" | "create_client" | "create_prospect"
    id?: string
    storage_folder_id?: string
    storage_folder_name?: string
  }
  household?: {
    full_name?: string
    first_name: string
    last_name: string
    email: string
    phone: string
    phone_type?: string
    phone_extension?: string
  }
}

export function createIntakeFormApplication(
  context: ApiContext,
  id: string,
  attributes: CreateIntakeFormApplicationAttributes
) {
  return apiContextFetch<IntakeFormSubmission>(
    context,
    bookResource(context, `intake_form_submissions/${encodeURIComponent(id)}/application`),
    {
      method: "POST",
      body: JSON.stringify({ application: attributes }),
    }
  )
}
